import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { MOCK_PRODUCTS } from '../../lib/mockData';

const EMPTY_FORM = {
  name: '',
  description: '',
  base_price: '',
  image_url: '',
  available: true,
  sort_order: 0,
  available_next_day: false,
  next_day_price: '',
};

function ProductCard({ product, onEdit, onToggle, onDelete, onMoveUp, onMoveDown, isFirst, isLast, deleting }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      className={`bg-white rounded-2xl border border-sage-100 shadow-soft overflow-hidden transition-opacity ${
        !product.available ? 'opacity-60' : ''
      }`}
    >
      {/* Image */}
      <div className="aspect-[4/3] bg-sage-50 relative overflow-hidden">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={`absolute inset-0 flex items-center justify-center text-5xl bg-sage-50 ${product.image_url ? 'hidden' : 'flex'}`}
        >
          🥙
        </div>

        {/* Available badge */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
            product.available
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-600 border border-red-200'
          }`}>
            {product.available ? 'Available' : 'Hidden'}
          </span>
          {product.available_next_day && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blush-100 text-blush-700 border border-blush-200">
              ⚡ Next-Day
            </span>
          )}
        </div>

        {/* Sort order controls */}
        <div className="absolute top-3 right-3 flex flex-col gap-1">
          <button
            onClick={() => onMoveUp(product)}
            disabled={isFirst}
            className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm text-sage-500 hover:text-charcoal disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move up"
          >
            <SafeIcon name="ChevronUp" />
          </button>
          <button
            onClick={() => onMoveDown(product)}
            disabled={isLast}
            className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm text-sage-500 hover:text-charcoal disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move down"
          >
            <SafeIcon name="ChevronDown" />
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-lg text-charcoal leading-tight">{product.name}</h3>
            <p className="text-xs text-sage-400 mt-1 line-clamp-2">{product.description}</p>
          </div>
          <span className="font-bold text-blush-500 text-lg shrink-0">${Number(product.base_price).toFixed(2)}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-sage-50">
          <button
            onClick={() => onEdit(product)}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl bg-sage-50 text-sage-600 hover:bg-sage-100 text-xs font-bold transition-colors"
          >
            <SafeIcon name="Pencil" /> Edit
          </button>
          <button
            onClick={() => onToggle(product)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold transition-colors ${
              product.available
                ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
          >
            <SafeIcon name={product.available ? 'EyeOff' : 'Eye'} />
            {product.available ? 'Hide' : 'Show'}
          </button>
          <button
            onClick={() => onDelete(product)}
            disabled={deleting === product.id}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-50 text-red-400 hover:bg-red-100 transition-colors disabled:opacity-50"
            title="Delete"
          >
            {deleting === product.id
              ? <div className="w-3 h-3 border-2 border-red-300 border-t-transparent rounded-full animate-spin" />
              : <SafeIcon name="Trash2" />
            }
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function ProductForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const nameRef = useRef();

  useEffect(() => {
    setForm(initial || EMPTY_FORM);
    setErrors({});
    setTimeout(() => nameRef.current?.focus(), 50);
  }, [initial]);

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.base_price || isNaN(Number(form.base_price)) || Number(form.base_price) <= 0)
      e.base_price = 'Enter a valid price';
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    // Only send columns that exist in the products schema.
    // The products table uses images (JSONB array), NOT an image_url column.
    onSave({
      name: form.name.trim(),
      description: form.description.trim(),
      base_price: Number(form.base_price),
      sort_order: Number(form.sort_order) || 0,
      available: form.available,
      available_next_day: form.available_next_day,
      next_day_price: form.next_day_price ? Number(form.next_day_price) : null,
      images: form.image_url ? [form.image_url] : [],
      sizes: [{ name: 'Standard', price: Number(form.base_price) }],
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="bg-white rounded-2xl border border-blush-100 shadow-soft p-6 space-y-5"
    >
      <h3 className="font-serif text-xl text-charcoal">
        {initial?.id ? 'Edit Product' : 'Add New Product'}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Name */}
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Product Name *
          </label>
          <input
            ref={nameRef}
            type="text"
            value={form.name}
            onChange={e => set('name', e.target.value)}
            placeholder="e.g. Kofta Kabob Skewers"
            className={`input-field ${errors.name ? 'border-red-300 focus:border-red-400' : ''}`}
          />
          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Description
          </label>
          <textarea
            rows={4}
            value={form.description}
            onChange={e => set('description', e.target.value)}
            placeholder="Short description shown on the menu card..."
            className="input-field resize-y"
          />
        </div>

        {/* Price */}
        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Price ($) *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.base_price}
            onChange={e => set('base_price', e.target.value)}
            placeholder="14.00"
            className={`input-field ${errors.base_price ? 'border-red-300' : ''}`}
          />
          {errors.base_price && <p className="text-xs text-red-500 mt-1">{errors.base_price}</p>}
        </div>

        {/* Sort order */}
        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Display Order
          </label>
          <input
            type="number"
            min="0"
            value={form.sort_order}
            onChange={e => set('sort_order', e.target.value)}
            placeholder="0"
            className="input-field"
          />
          <p className="text-[10px] text-sage-400 mt-1">Lower number = shown first</p>
        </div>

        {/* Image URL */}
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Image URL
          </label>
          <input
            type="url"
            value={form.image_url}
            onChange={e => set('image_url', e.target.value)}
            placeholder="https://... or /images/kofta-skewers.png"
            className="input-field"
          />
          {form.image_url && (
            <div className="mt-2 w-20 h-20 rounded-xl overflow-hidden border border-sage-100 bg-sage-50">
              <img
                src={form.image_url}
                alt="preview"
                className="w-full h-full object-cover"
                onError={e => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
        </div>

        {/* Available toggle */}
        <div className="md:col-span-2 flex items-center gap-3">
          <button
            type="button"
            onClick={() => set('available', !form.available)}
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
              form.available ? 'bg-green-400' : 'bg-sage-200'
            }`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
              form.available ? 'translate-x-6' : 'translate-x-0'
            }`} />
          </button>
          <span className="text-sm font-medium text-charcoal">
            {form.available ? 'Available — shown to customers' : 'Hidden — not shown to customers'}
          </span>
        </div>

        {/* Next-day delivery toggle */}
        <div className="md:col-span-2 flex items-center gap-3 pt-2 border-t border-sage-100">
          <button
            type="button"
            onClick={() => set('available_next_day', !form.available_next_day)}
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
              form.available_next_day ? 'bg-blush-300' : 'bg-sage-200'
            }`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
              form.available_next_day ? 'translate-x-6' : 'translate-x-0'
            }`} />
          </button>
          <span className="text-sm font-medium text-charcoal">
            {form.available_next_day ? 'Available for next-day delivery' : 'Not available next-day'}
          </span>
        </div>

        {/* Next-day price (conditional) */}
        {form.available_next_day && (
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
              Next-Day Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.next_day_price}
              onChange={e => set('next_day_price', e.target.value)}
              placeholder={`Leave empty to use base price (${form.base_price || '—'})`}
              className="input-field"
            />
            <p className="text-[10px] text-sage-400 mt-1">Custom price for next-day orders (usually higher)</p>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="btn-primary flex items-center gap-2"
        >
          {saving
            ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
            : <><SafeIcon name="Check" /> {initial?.id ? 'Save Changes' : 'Add Product'}</>
          }
        </button>
        <button onClick={onCancel} disabled={saving} className="btn-secondary">
          Cancel
        </button>
      </div>
    </motion.div>
  );
}

export default function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [toast, setToast] = useState('');

  useEffect(() => { fetchProducts(); }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  async function fetchProducts() {
    setLoading(true);
    if (isSupabaseConfigured()) {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('sort_order');
      setProducts(error ? MOCK_PRODUCTS : (data || MOCK_PRODUCTS));
    } else {
      setProducts(MOCK_PRODUCTS);
    }
    setLoading(false);
  }

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (isSupabaseConfigured()) {
        // Strip to only columns that exist in the products schema.
        // Never send image_url — the table uses images (JSONB) not image_url.
        const { image_url, ...rest } = formData;
        const payload = {
          name:        rest.name,
          description: rest.description ?? '',
          base_price:  rest.base_price,
          sort_order:  rest.sort_order ?? 0,
          available:   rest.available ?? true,
          images:      rest.images ?? [],
          sizes:       rest.sizes ?? [{ name: 'Standard', price: rest.base_price }],
          available_next_day: rest.available_next_day ?? false,
          next_day_price: rest.next_day_price ? Number(rest.next_day_price) : null,
        };

        if (editingProduct?.id && !editingProduct.id.includes('-mock')) {
          const { error } = await supabase
            .from('products')
            .update(payload)
            .eq('id', editingProduct.id);
          if (error) throw error;
          showToast('Product updated ✓');
        } else {
          const { error } = await supabase
            .from('products')
            .insert(payload);
          if (error) throw error;
          showToast('Product added ✓');
        }
        await fetchProducts();
      } else {
        // Mock mode — update local state
        if (editingProduct?.id) {
          setProducts(prev => prev.map(p => p.id === editingProduct.id ? { ...p, ...formData } : p));
        } else {
          setProducts(prev => [...prev, { ...formData, id: `mock-${Date.now()}` }]);
        }
        showToast('Saved (mock mode) ✓');
      }
      setShowForm(false);
      setEditingProduct(null);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct({
      ...product,
      image_url: product.image_url || product.images?.[0] || '',
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggleAvailable = async (product) => {
    const updated = { ...product, available: !product.available };
    setProducts(prev => prev.map(p => p.id === product.id ? updated : p));
    if (isSupabaseConfigured()) {
      await supabase.from('products').update({ available: updated.available }).eq('id', product.id);
    }
    showToast(updated.available ? 'Product is now visible ✓' : 'Product hidden from menu ✓');
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    setDeleting(product.id);
    try {
      if (isSupabaseConfigured()) {
        await supabase.from('products').delete().eq('id', product.id);
      }
      setProducts(prev => prev.filter(p => p.id !== product.id));
      showToast('Product deleted ✓');
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setDeleting(null);
    }
  };

  const handleMoveUp = async (product) => {
    const idx = products.findIndex(p => p.id === product.id);
    if (idx === 0) return;
    const reordered = [...products];
    [reordered[idx - 1], reordered[idx]] = [reordered[idx], reordered[idx - 1]];
    const updated = reordered.map((p, i) => ({ ...p, sort_order: i }));
    setProducts(updated);
    if (isSupabaseConfigured()) {
      await Promise.all(updated.map(p =>
        supabase.from('products').update({ sort_order: p.sort_order }).eq('id', p.id)
      ));
    }
  };

  const handleMoveDown = async (product) => {
    const idx = products.findIndex(p => p.id === product.id);
    if (idx === products.length - 1) return;
    const reordered = [...products];
    [reordered[idx], reordered[idx + 1]] = [reordered[idx + 1], reordered[idx]];
    const updated = reordered.map((p, i) => ({ ...p, sort_order: i }));
    setProducts(updated);
    if (isSupabaseConfigured()) {
      await Promise.all(updated.map(p =>
        supabase.from('products').update({ sort_order: p.sort_order }).eq('id', p.id)
      ));
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  return (
    <div className="space-y-6">

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-6 right-6 z-50 bg-green-600 text-white px-5 py-3 rounded-2xl shadow-lg font-bold text-sm flex items-center gap-2"
          >
            <SafeIcon name="CheckCircle" /> {toast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl text-charcoal">Menu Items</h2>
          <p className="text-sage-500 text-sm mt-0.5">
            {products.filter(p => p.available).length} available · {products.length} total
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setEditingProduct(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2"
          >
            <SafeIcon name="Plus" /> Add Product
          </button>
        )}
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <ProductForm
            initial={editingProduct}
            onSave={handleSave}
            onCancel={handleCancelForm}
            saving={saving}
          />
        )}
      </AnimatePresence>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blush-300" />
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sage-100 p-12 text-center space-y-3">
          <div className="text-5xl">🥙</div>
          <p className="font-serif text-xl text-charcoal">No products yet</p>
          <p className="text-sage-400 text-sm">Add your first menu item to get started.</p>
          <button
            onClick={() => { setEditingProduct(null); setShowForm(true); }}
            className="btn-primary inline-flex items-center gap-2 mt-2"
          >
            <SafeIcon name="Plus" /> Add First Product
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence>
            {products.map((product, idx) => (
              <ProductCard
                key={product.id}
                product={product}
                onEdit={handleEdit}
                onToggle={handleToggleAvailable}
                onDelete={handleDelete}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                isFirst={idx === 0}
                isLast={idx === products.length - 1}
                deleting={deleting}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
